/**
 * Choppiness Index
 *
 * Measures the market's tendency to trend or move sideways.
 * Values 0-38.2: trending, 61.8-100: choppy/sideways.
 * Formula: 100 * LOG10(SUM(ATR,n) / (HighestHigh - LowestLow)) / LOG10(n)
 *
 * Category: Volatility | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeATR } from "./atr";

export const choppinessIndicator: IndicatorDefinition = {
  id: "choppiness",
  name: "Choppiness Index",
  shortName: "CHOP",
  category: "volatility",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 14, min: 2, max: 100 },
  ],
  plots: [
    { key: "chop", label: "CHOP", color: "#AB47BC", style: "line", lineWidth: 2 },
    { key: "upper", label: "Upper", color: "#555555", style: "line", lineWidth: 1 },
    { key: "lower", label: "Lower", color: "#555555", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period } = params;
    const chopData: { time: number; value: number }[] = [];
    const upperData: { time: number; value: number }[] = [];
    const lowerData: { time: number; value: number }[] = [];

    // Compute True Range values
    const trValues: number[] = [];
    for (let i = 0; i < candles.length; i++) {
      if (i === 0) {
        trValues.push(candles[i].high - candles[i].low);
      } else {
        const tr = Math.max(
          candles[i].high - candles[i].low,
          Math.abs(candles[i].high - candles[i - 1].close),
          Math.abs(candles[i].low - candles[i - 1].close)
        );
        trValues.push(tr);
      }
    }

    const log10n = Math.log10(period);

    for (let i = period; i < candles.length; i++) {
      let atrSum = 0;
      let highestHigh = -Infinity;
      let lowestLow = Infinity;

      for (let j = i - period + 1; j <= i; j++) {
        atrSum += trValues[j];
        if (candles[j].high > highestHigh) highestHigh = candles[j].high;
        if (candles[j].low < lowestLow) lowestLow = candles[j].low;
      }

      const range = highestHigh - lowestLow;
      if (range > 0) {
        const chop = 100 * Math.log10(atrSum / range) / log10n;
        chopData.push({ time: candles[i].time, value: chop });
        upperData.push({ time: candles[i].time, value: 61.8 });
        lowerData.push({ time: candles[i].time, value: 38.2 });
      }
    }

    return [
      { key: "chop", data: chopData },
      { key: "upper", data: upperData },
      { key: "lower", data: lowerData },
    ];
  },
};
